import type { ReactElement } from "react";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { ErrorState } from "./state-components/error-state";
import { EmptyState } from "./state-components/empty-state";
import { usePageLayoutState } from "./hooks/use-page-layout-state";
import type { DashboardPageLayoutProps } from "./types";

/**
 * DashboardPageLayout - Reusable layout for dashboard pages
 *
 * Handles loading, error, empty, and success states automatically.
 * Used for pages with statistics, cards, and summary widgets.
 * No back navigation (dashboards are top-level pages).
 *
 * @example
 * ```tsx
 * const StudentDashboard = () => {
 *   const { data, fetching } = useAppSelector(selectStudentSummary);
 *
 *   return (
 *     <DashboardPageLayout
 *       fetching={fetching}
 *       data={data}
 *       loadingComponent={<DashboardSkeleton />}
 *       emptyStateProps={{
 *         title: "Bienvenue !",
 *         description: "Votre tableau de bord est vide pour le moment."
 *       }}
 *     >
 *       {(summary) => <DashboardContent summary={summary} />}
 *     </DashboardPageLayout>
 *   );
 * };
 * ```
 */
export function DashboardPageLayout<T>({
  fetching,
  data,
  children,
  loadingComponent,
  errorStateProps,
  emptyStateProps,
  className,
}: DashboardPageLayoutProps<T>): ReactElement {
  const hasData = data !== null && data !== undefined;
  const { isLoading, isError, isSuccess } = usePageLayoutState({
    fetching,
    idleAsLoading: true,
  });

  // Render loading state
  if (isLoading) {
    return <div className={cn("space-y-4", className)}>{loadingComponent}</div>;
  }

  // Render error state
  if (isError) {
    return (
      <div className={cn("space-y-4", className)}>
        <ErrorState {...errorStateProps} />
      </div>
    );
  }

  // Render empty state (for newly registered users)
  if (isSuccess && !hasData) {
    return (
      <div className={cn("space-y-4", className)}>
        <EmptyState {...emptyStateProps} />
      </div>
    );
  }

  // Render success state with data
  if (hasData) {
    return <div className={cn("space-y-4", className)}>{children(data)}</div>;
  }

  // Fallback: idle state without idleAsLoading
  return <div className={cn("space-y-4", className)}>{loadingComponent}</div>;
}
